import type { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { UserSelectors } from '@core/account-data';
import { filterBoolean, Helpers } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { referencesToActionsFactory } from '@core/ngrx-legacy';
import { JobsWrapperActionsService, StageActions } from '@core/offers-data';
import type { JobPlacement } from '@core/placements-types';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import type { PlacementDisqualifyParams, PlacementHttpReference } from './placement-http-client';
import { PlacementHttpClient } from './placement-http-client';
import { PlacementActions } from './placement.store';

@Injectable({ providedIn: 'root' })
export class PlacementEffects {
  private readonly jobsWrapperActionsService = inject(JobsWrapperActionsService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly placementHttpClient = inject(PlacementHttpClient);
  private readonly i18n = inject(I18nService);
  private readonly dialogService = inject(DialogService);

  public getReferencesActions = referencesToActionsFactory<PlacementHttpReference>()([
    { type: 'Offer', action: this.jobsWrapperActionsService.briefReferenceAction },
    { type: 'Stage', action: StageActions.references },
  ]);

  public create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.createOnePessimistic),
      mergeMap(({ payload: { candidateId, offerId, talentPoolId, locationIds, correlationId } }) =>
        this.placementHttpClient.create({ candidateId, offerId, talentPoolId, locationIds }).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.createOnePessimisticSuccess({
              payload: { data: placement, correlationId },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.createOnePessimisticFailed({ payload: { error, correlationId } })),
          ),
        ),
      ),
    ),
  );

  public delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.removeOnePessimistic),
      mergeMap(({ payload }) =>
        this.placementHttpClient.delete({ id: <number>payload.data }).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.removeOnePessimisticSuccess({ payload: { data: placement } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              PlacementActions.removeOnePessimisticFailed({
                payload: { error, data: payload.data },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updatePositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.updatePositions),
      mergeMap(({ payload: { positions } }) =>
        this.placementHttpClient.updatePositions(positions).pipe(
          switchMap(({ references, placements }) => [
            ...this.getReferencesActions(references),
            PlacementActions.updatePositionsSuccess({ payload: { placements } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.updatePositionsFailed({ payload: { error, positions } })),
          ),
        ),
      ),
    ),
  );

  public updateCandidatePositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.updateCandidatePositions),
      mergeMap(({ payload: { positions } }) =>
        this.placementHttpClient.updateCandidatePositions(positions).pipe(
          switchMap(({ references, placements }) => [
            ...this.getReferencesActions(references),
            PlacementActions.updateCandidatePositionsSuccess({ payload: { placements } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.updateCandidatePositionsFailed({ payload: { error, positions } })),
          ),
        ),
      ),
    ),
  );

  public requalify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.requalify),
      mergeMap(({ payload }) =>
        this.placementHttpClient.requalify(payload.id).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.requalifySuccess({ payload: { placement } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.requalifyFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public disqualify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.disqualify),
      filterBoolean,
      mergeMap(({ payload: { params } }: { payload: { params: PlacementDisqualifyParams } }) =>
        this.placementHttpClient.disqualify(params).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.disqualifySuccess({ payload: { placement } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.disqualifyFailed({ payload: { error, params } })),
          ),
        ),
      ),
    ),
  );

  public changePreferredLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.changePreferredWorkLocations),
      mergeMap(({ payload: { placementId, locationIds } }) =>
        this.placementHttpClient.updatePreferredLocations(placementId, locationIds).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.changePreferredWorkLocationsSuccess({
              payload: { placement },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.changePreferredWorkLocationsFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  public changeStage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.changeStage),
      mergeMap(({ payload: { params, changedHireStatus } }) =>
        this.placementHttpClient.changeStage(params).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.changeStageSuccess({ payload: { placement, changedHireStatus } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.changeStageFailed({ payload: { error, params } })),
          ),
        ),
      ),
    ),
  );

  public changePosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.changePosition),
      mergeMap(({ payload: { params } }) =>
        this.placementHttpClient.changePosition(params).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.changePositionSuccess({ payload: { placement } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.changePositionFailed({ payload: { error, params } })),
          ),
        ),
      ),
    ),
  );

  public fetchHiringDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.fetchHiringDetails),
      mergeMap(({ payload: { placementId } }) =>
        this.placementHttpClient.fetchHiringDetails(placementId).pipe(
          concatLatestFrom(({ placement }) =>
            this.store.select(UserSelectors.byId(placement.hiredById ?? null)),
          ),
          switchMap(([{ placement, references }, user]) => {
            const placementWithReferences: JobPlacement = Helpers.fillInReferences(
              {
                ...placement,
                hiredBy: user,
              },
              references,
            );
            return [
              ...this.getReferencesActions(references),
              PlacementActions.fetchHiringDetailsSuccess({
                payload: { placement: placementWithReferences },
              }),
            ];
          }),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.fetchHiringDetailsFailed({ payload: { error, placementId } })),
          ),
        ),
      ),
    ),
  );

  public updateHiringDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.updateHiringDetails),
      mergeMap(({ payload: { params } }) =>
        this.placementHttpClient.updateHiringDetails(params).pipe(
          concatLatestFrom(({ placement }) =>
            this.store.select(UserSelectors.byId(placement.hiredById ?? null)),
          ),
          switchMap(([{ placement, references }, user]) => {
            const placementWithReferences: JobPlacement = Helpers.fillInReferences(
              {
                ...placement,
                hiredBy: user,
              },
              references,
            );
            return [
              ...this.getReferencesActions(references),
              PlacementActions.updateHiringDetailsSuccess({
                payload: { placement: placementWithReferences },
              }),
            ];
          }),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.updateHiringDetailsFailed({ payload: { error, params } })),
          ),
        ),
      ),
    ),
  );

  private showDisqualifyDialog(): Observable<boolean> {
    return this.dialogService.warning({
      width: DialogWidth.SmallXX,
      title: this.i18n.translate('app.candidate.disqualify_dialog.title'),
      message: this.i18n.translate('app.candidate.disqualify_dialog.message_html', { count: 1 }),
      agreeLabel: this.i18n.translate('app.candidate.disqualify_dialog.agree_label'),
    });
  }
}
